import type { SourceSeries } from '@panelvault/shared';
import { Search } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/controls';
import { Dialog } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { ApiError, coverPreviewUrl } from '../../lib/api';
import { coverGradient } from '../../lib/cover';
import { useImportSeries, useSeriesSearch } from './queries';

interface ImportSeriesDialogProps {
  open: boolean;
  onClose: () => void;
  onImported: (seriesId: string) => void;
}

/** Waits until typing pauses, so every keystroke is not a request. */
function useDebounced(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export function ImportSeriesDialog({ open, onClose, onImported }: ImportSeriesDialogProps) {
  const { t } = useTranslation();
  const ids = useId();
  const importSeries = useImportSeries();

  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<SourceSeries | null>(null);
  const [includeIssues, setIncludeIssues] = useState(true);
  const [includeCovers, setIncludeCovers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A partial import (series in, run not) keeps the dialog open to explain it.
  const [partial, setPartial] = useState<{ seriesId: string; warning: string } | null>(null);

  const debounced = useDebounced(query, 400);
  const search = useSeriesSearch(open ? debounced : '');

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelected(null);
    setIncludeIssues(true);
    setIncludeCovers(false);
    setPartial(null);
    setError(null);
  }, [open]);

  async function submit() {
    if (!selected) return;
    setError(null);

    try {
      const result = await importSeries.mutateAsync({
        source: selected.source,
        externalId: selected.externalId,
        includeIssues,
        includeCovers,
      });
      if (result.warning) {
        setPartial({ seriesId: result.seriesId, warning: result.warning });
        return;
      }
      onImported(result.seriesId);
      onClose();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('form.saveFailed'));
    }
  }

  const noSearchSource =
    search.data && search.data.searched.length === 0 && debounced.trim().length >= 2;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('import.title')}
      description={t('import.description')}
      className="sm:max-w-2xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={importSeries.isPending}>
            {t('form.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={!selected || importSeries.isPending}>
            {importSeries.isPending ? t('import.running') : t('import.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted" />
          <Input
            id={`${ids}-query`}
            className="pl-9"
            placeholder={t('import.searchPlaceholder')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            autoFocus
          />
        </div>

        {noSearchSource ? (
          <p className="rounded-lg border bg-surface-raised p-3 text-sm text-muted">
            {t('import.noSource')}{' '}
            <Link to="/settings" className="text-accent underline" onClick={onClose}>
              {t('nav.settings')}
            </Link>
          </p>
        ) : null}

        {search.isFetching ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        ) : null}

        {search.data?.searched.some((note) => note.status === 'error') ? (
          <p className="text-sm text-danger">
            {search.data.searched
              .filter((note) => note.status === 'error')
              .map((note) => `${note.source}: ${note.message ?? ''}`)
              .join(' · ')}
          </p>
        ) : null}

        <ul className="flex max-h-80 flex-col gap-2 overflow-y-auto">
          {search.data?.results.map((result) => {
            const isSelected =
              selected?.source === result.source && selected.externalId === result.externalId;

            return (
              <li key={`${result.source}-${result.externalId}`}>
                <button
                  type="button"
                  onClick={() => setSelected(result)}
                  className={`flex w-full items-center gap-3 rounded-lg border p-2 text-left transition-colors ${
                    isSelected ? 'border-accent bg-surface-raised' : 'hover:bg-surface-raised'
                  }`}
                >
                  <span
                    className="aspect-[2/3] w-10 shrink-0 overflow-hidden rounded border"
                    style={
                      result.coverUrl ? undefined : { backgroundImage: coverGradient(result.name) }
                    }
                  >
                    {result.coverUrl ? (
                      <img
                        src={coverPreviewUrl(result.source, result.coverUrl)}
                        alt=""
                        loading="lazy"
                        className="size-full object-cover"
                      />
                    ) : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-foreground">{result.name}</span>
                    <span className="block truncate text-xs text-muted">
                      {[
                        result.publisherName,
                        result.startYear?.toString(),
                        result.issueCount
                          ? t('import.issueCount', { count: result.issueCount })
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <Badge variant="outline">{result.source}</Badge>
                </button>
              </li>
            );
          })}
        </ul>

        {debounced.trim().length >= 2 &&
        !search.isFetching &&
        search.data &&
        search.data.results.length === 0 &&
        !noSearchSource ? (
          <p className="text-sm text-muted">{t('import.nothingFound')}</p>
        ) : null}

        {selected ? (
          <div className="flex flex-col gap-2 rounded-lg border bg-surface-raised p-3">
            <Checkbox
              label={t('import.includeIssues')}
              checked={includeIssues}
              onChange={(event) => setIncludeIssues(event.target.checked)}
            />
            <Checkbox
              label={t('import.includeCovers')}
              checked={includeCovers}
              onChange={(event) => setIncludeCovers(event.target.checked)}
            />
            <p className="text-xs text-muted">{t('import.protectHint')}</p>
          </div>
        ) : null}

        {partial ? (
          <div
            role="status"
            className="flex flex-col gap-2 rounded-lg border border-accent bg-surface-raised p-3 text-sm"
          >
            <p className="text-foreground">{partial.warning}</p>
            <Button
              size="sm"
              className="self-start"
              onClick={() => {
                onImported(partial.seriesId);
                onClose();
              }}
            >
              {t('import.openSeries')}
            </Button>
          </div>
        ) : null}

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
